import { Fragment, useState, useEffect } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useChain } from '@/stores';
import { CHAINS, isChainId } from '@/config/chains';
import { tradingAPI } from '@/services/api/endpoints/trading';
import { formatFixed } from '@/utils/formatting/numbers';
import { resolveChainChangePath } from '@/utils/chainNavigation';
import type { ChainId } from '@/stores/slices/chainSlice';
import type { NativeBalance } from '@/types/api/trading';

interface ChainSelectorProps {
  variant?: 'full' | 'compact';
  className?: string;
  menuDirection?: 'down' | 'up';
}

export const ChainSelector = ({
  variant = 'full',
  className = '',
  menuDirection = 'down',
}: ChainSelectorProps) => {
  const { selectedChain, setSelectedChain } = useChain();
  const navigate = useNavigate();
  const location = useLocation();
  const routeChainMatch = location.pathname.match(/^\/data-explorer\/([^/]+)(\/|$)/);
  const routeChainOverride = routeChainMatch && isChainId(routeChainMatch[1])
    ? routeChainMatch[1]
    : null;
  const effectiveChain = routeChainOverride || selectedChain;

  const currentChain = CHAINS.find((c) => c.id === effectiveChain) || CHAINS[0];

  const [nativeBalances, setNativeBalances] = useState<Record<string, NativeBalance>>({});

  useEffect(() => {
    let cancelled = false;
    tradingAPI.getNativeBalances()
      .then((res) => {
        if (!cancelled) setNativeBalances(res.balances);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const getBalanceForChain = (numericId: number): NativeBalance | null => {
    return nativeBalances[String(numericId)] ?? null;
  };

  const formatBalance = (bal: NativeBalance | null): string | null => {
    if (!bal) return null;
    return `${formatFixed(bal.balance_tokens, 4)} ${bal.symbol}`;
  };

  const currentBalance = getBalanceForChain(currentChain.numericId);

  const handleChainChange = (chainId: ChainId) => {
    if (chainId === selectedChain && !routeChainOverride) return;

    // Persist user-selected default chain.
    if (chainId !== selectedChain) {
      setSelectedChain(chainId);
    }

    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    const nextPath = resolveChainChangePath(location, chainId);
    if (nextPath && nextPath !== currentPath) {
      navigate(nextPath);
    }
  };

  return (
    <Menu
      as="div"
      className={cn('relative text-left', variant === 'full' && 'inline-block', variant === 'compact' && 'h-full', className)}
    >
      <Menu.Button
        className={cn(
          variant === 'full' &&
            'inline-flex items-center justify-center gap-1.5 rounded-lg border bg-white px-3 py-1.5 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50 dark:border-white/[0.07] dark:bg-white/[0.04] dark:text-gray-100 dark:hover:border-white/[0.14] dark:hover:bg-white/[0.08]',
          variant === 'compact' &&
            'flex h-full w-full flex-col items-center justify-center gap-0.5 text-xs font-medium text-gray-500 transition-colors hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 focus:outline-none'
        )}
      >
        <img src={currentChain.icon} alt={`${currentChain.name} icon`} className="h-5 w-5 object-contain" />
        {variant === 'full' ? (
          <>
            <span>{currentChain.shortName}</span>
            {currentBalance && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {formatBalance(currentBalance)}
              </span>
            )}
            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
          </>
        ) : (
          <span>{currentBalance ? formatBalance(currentBalance) : 'Chain'}</span>
        )}
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={cn(
            'absolute right-0 z-50 w-56 rounded-xl border bg-white shadow-lg focus:outline-none dark:border-white/[0.08] dark:bg-abyss-100/95 dark:shadow-card-soft dark:backdrop-blur-xl',
            menuDirection === 'up' ? 'bottom-full mb-2 origin-bottom-right' : 'mt-2 origin-top-right'
          )}
        >
          <div className="p-1">
            {CHAINS.map((chain) => {
              const bal = getBalanceForChain(chain.numericId);
              return (
                <Menu.Item key={chain.id}>
                  {({ active }) => (
                    <button
                      onClick={() => handleChainChange(chain.id)}
                      className={cn(
                        'group flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors',
                        active
                          ? 'bg-gray-100 dark:bg-white/[0.05]'
                          : 'text-gray-900 dark:text-gray-100',
                        effectiveChain === chain.id && 'bg-gray-100 dark:bg-violet-500/15 dark:text-violet-100'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <img src={chain.icon} alt={`${chain.name} icon`} className="h-5 w-5 object-contain" />
                        <div className="text-left">
                          <div className="text-sm font-medium">{chain.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {bal ? formatBalance(bal) : chain.shortName}
                          </div>
                        </div>
                      </div>
                      {effectiveChain === chain.id && (
                        <CheckIcon className="h-4 w-4 text-gray-400 dark:text-violet-300" />
                      )}
                    </button>
                  )}
                </Menu.Item>
              );
            })}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};
